/**
 * Countdown de la caja de compra.
 *
 * El original de femprobiotics lo declara persistente:
 * `data-instant-countdown-is-persistent="true"`, o sea que la cuenta sobrevive a
 * la recarga y no se reinicia. Aqui la persistencia es un setting con tres
 * grados, porque cada uno dice una cosa distinta al cliente:
 *
 *   session — la cuenta vive en sessionStorage: cada visita empieza de nuevo
 *   device  — localStorage: sobrevive entre visitas, como el original
 *   off     — sin memoria: cada carga arranca el reloj
 *
 * Lo que pasa al llegar a cero tampoco esta verificado en el original (ver
 * docs/research/femprobiotics.co/BEHAVIORS.md), asi que tambien es un setting.
 * Por defecto la linea se oculta: un reloj congelado en 00:00 se lee como roto y
 * uno que se reinicia solo delata que el plazo no era real.
 */
class OfferCountdown extends HTMLElement {
  #output = null;
  #deadline = 0;
  #timer = 0;

  connectedCallback() {
    this.#output = this.querySelector('[data-countdown-value]');
    if (!this.#output) return;

    this.#deadline = this.#readDeadline() ?? this.#startNewDeadline();
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  get #minutes() {
    return Number(this.dataset.minutes) || 10;
  }

  get #store() {
    try {
      if (this.dataset.persist === 'device') return localStorage;
      if (this.dataset.persist === 'session') return sessionStorage;
    } catch {
      // Navegacion privada o cookies bloqueadas: se cuenta sin memoria.
    }
    return null;
  }

  get #key() {
    return `ritualic:countdown:${this.dataset.key ?? 'default'}`;
  }

  #readDeadline() {
    try {
      const saved = Number(this.#store?.getItem(this.#key));
      return Number.isFinite(saved) && saved > 0 ? saved : null;
    } catch {
      return null;
    }
  }

  #startNewDeadline() {
    const deadline = Date.now() + this.#minutes * 60 * 1000;
    try {
      this.#store?.setItem(this.#key, String(deadline));
    } catch {
      // Sin almacenamiento el contador sigue funcionando, solo no recuerda.
    }
    return deadline;
  }

  #tick() {
    const restante = this.#deadline - Date.now();

    if (restante > 0) {
      this.#render(restante);
      return;
    }

    if (this.dataset.onExpire === 'restart') {
      this.#deadline = this.#startNewDeadline();
      this.#render(this.#deadline - Date.now());
      return;
    }

    clearInterval(this.#timer);
    this.#render(0);
    if (this.dataset.onExpire !== 'zero') this.hidden = true;
  }

  #render(restante) {
    const total = Math.max(0, Math.round(restante / 1000));
    const minutos = Math.floor(total / 60);
    const segundos = total % 60;
    const texto = `${String(minutos).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`;

    if (this.#output.textContent !== texto) this.#output.textContent = texto;
    this.#output.setAttribute('datetime', `PT${minutos}M${segundos}S`);
  }
}

if (!customElements.get('offer-countdown')) customElements.define('offer-countdown', OfferCountdown);
